import { useState } from 'react'
import { Eye, EyeOff, Copy, Check, Pencil, Trash2, ExternalLink, Loader2, StickyNote } from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { passwordsApi } from '../api'
import type { Password } from '../types'

interface PasswordCardProps {
  password: Password
  onEdit: (password: Password) => void
  onDelete: (id: number) => void
  compact?: boolean
}

export function PasswordCard({ password, onEdit, onDelete, compact }: PasswordCardProps) {
  const [showPassword, setShowPassword] = useState(false)
  const [decryptedPassword, setDecryptedPassword] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState<'username' | 'password' | null>(null)

  const handleShowPassword = async () => {
    if (showPassword) {
      setShowPassword(false)
      return
    }

    if (decryptedPassword) {
      setShowPassword(true)
      return
    }

    setLoading(true)
    try {
      const detail = await passwordsApi.getOne(password.id)
      setDecryptedPassword(detail.password)
      setShowPassword(true)
    } catch {
      toast.error('Error al obtener la contraseña')
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = async (text: string, type: 'username' | 'password') => {
    // Si es contraseña y no la hemos cargado aún
    if (type === 'password' && !decryptedPassword) {
      setLoading(true)
      try {
        const detail = await passwordsApi.getOne(password.id)
        setDecryptedPassword(detail.password)
        text = detail.password
      } catch {
        toast.error('Error al obtener la contraseña')
        setLoading(false)
        return
      }
      setLoading(false)
    } else if (type === 'password' && decryptedPassword) {
      text = decryptedPassword
    }

    const success = await passwordsApi.copyToClipboard(text)
    if (success) {
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
      toast.success(`${type === 'username' ? 'Usuario' : 'Contraseña'} copiado`)
    } else {
      toast.error('Error al copiar')
    }
  }

  // Modo compacto: sin Card wrapper, diseño más simple
  if (compact) {
    return (
      <div className="grid grid-cols-[1fr_1fr_1fr_auto] items-center py-2 px-3 rounded-lg bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/50 dark:hover:bg-gray-800 transition-colors">
        {/* Nombre - izquierda */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium truncate">{password.nombre}</span>
          {password.url && (
            <a
              href={password.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground shrink-0"
            >
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>

        {/* Usuario - centro */}
        <div className="flex items-center justify-center gap-1 min-w-0">
          {password.username ? (
            <>
              <span className="text-sm font-mono truncate">{password.username}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 shrink-0"
                onClick={() => handleCopy(password.username, 'username')}
              >
                {copied === 'username' ? (
                  <Check className="h-3 w-3 text-teal-500" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </Button>
            </>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </div>

        {/* Contraseña - derecha */}
        <div className="flex items-center justify-end gap-1">
          <span className="text-sm font-mono">
            {showPassword && decryptedPassword ? decryptedPassword : '••••••••'}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0"
            onClick={handleShowPassword}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : showPassword ? (
              <EyeOff className="h-3 w-3" />
            ) : (
              <Eye className="h-3 w-3" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0"
            onClick={() => handleCopy('', 'password')}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : copied === 'password' ? (
              <Check className="h-3 w-3 text-teal-500" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </Button>
        </div>

        {/* Acciones */}
        <div className="flex items-center gap-1 pl-24">
          {password.notas && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground"
              title={password.notas}
            >
              <StickyNote className="h-3 w-3" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onEdit(password)}
            title="Editar"
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive"
            onClick={() => onDelete(password.id)}
            title="Eliminar"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    )
  }

  // Modo normal con Card
  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardContent className="p-4">
        <div className="grid grid-cols-[200px_1fr_1fr_auto_auto] items-center gap-4">
          {/* Título - ancho fijo */}
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-semibold text-lg truncate">{password.nombre}</h3>
            {password.url && (
              <a
                href={password.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground shrink-0"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>

          {/* Usuario */}
          <div className="flex items-center gap-2 min-w-0">
            {password.username ? (
              <>
                <span className="text-sm text-muted-foreground shrink-0">Usuario:</span>
                <span className="text-sm font-mono truncate">{password.username}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  onClick={() => handleCopy(password.username, 'username')}
                >
                  {copied === 'username' ? (
                    <Check className="h-3 w-3 text-teal-500" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </div>

          {/* Contraseña */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground shrink-0">Contraseña:</span>
            <span className="text-sm font-mono">
              {showPassword && decryptedPassword ? decryptedPassword : '••••••••'}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={handleShowPassword}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : showPassword ? (
                <EyeOff className="h-3 w-3" />
              ) : (
                <Eye className="h-3 w-3" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={() => handleCopy('', 'password')}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : copied === 'password' ? (
                <Check className="h-3 w-3 text-teal-500" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          </div>

          {/* Notas */}
          <div className="hidden md:block">
            {password.notas ? (
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 max-w-[200px] text-muted-foreground"
                title={password.notas}
              >
                <StickyNote className="h-4 w-4 shrink-0" />
                <span className="truncate">{password.notas}</span>
              </Button>
            ) : null}
          </div>

          {/* Acciones */}
          <div className="flex gap-1 shrink-0">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => onEdit(password)}
              title="Editar"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => onDelete(password.id)}
              title="Eliminar"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
